
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { WeeklyMenu, ShoppingList } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useRecipes } from '@/contexts/RecipesContext';
import useUserProfileSync from '@/hooks/useUserProfileSync';

import { useShoppingList } from './useShoppingList';
import { useMenuSync } from './useMenuSync';
import { useMenuOperations } from './useMenuOperations';
import { getMealTypes, getMealCalorieSuggestion } from './menuUtils';
import { calculateProteinFromWeight } from './menuUtils';

interface MenuContextType {
  weeklyMenu: WeeklyMenu | null;
  loading: boolean;
  shoppingList: ShoppingList | null;
  addRecipeToMenu: (recipeId: string, day: string, mealType: string, portionSize: number) => Promise<void>;
  removeRecipeFromMenu: (recipeId: string, day: string, mealType: string, index: number) => Promise<void>;
  clearMenu: () => Promise<void>;
  getMenuTotals: (day: string) => { calories: number; protein: number; carbs: number; fat: number; iron: number; calcium: number; zinc: number; magnesium: number; omega3: number; } | null;
  availableMealTypes: string[];
  getMealCalorieSuggestion: (mealType: string) => number;
  addCustomIngredient: (day: string, ingredient: any) => Promise<void>;
  updateMenu: (updatedMenu: WeeklyMenu) => Promise<void>;
  toggleShoppingItem: (itemId: string) => Promise<void>;
  refreshMenu: () => Promise<void>;
  forceUpdate: number;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

interface MenuProviderProps {
  children: React.ReactNode;
}

export const MenuProvider: React.FC<MenuProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { recipes } = useRecipes();
  const [availableMealTypes, setAvailableMealTypes] = useState<string[]>(getMealTypes(3));
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Use the profile sync hook
  const { getSyncedProfile } = useUserProfileSync({
    userId: user?.id,
    enabled: !!user?.id
  });
  
  const { 
    shoppingList, 
    toggleShoppingItem, 
    generateShoppingList,
    resetShoppingList
  } = useShoppingList(user?.id);
  
  const { 
    weeklyMenu, 
    loading, 
    updateMenu, 
    refreshMenu,
    loadMenu
  } = useMenuSync(user?.id, async () => {
    console.log('🔄 Menu changed, regenerating shopping list...');
    if (weeklyMenu && recipes) {
      console.log('📋 Calling generateShoppingList with:', { 
        weeklyMenuExists: !!weeklyMenu, 
        recipesCount: recipes?.length || 0 
      });
      const result = await generateShoppingList(weeklyMenu, recipes);
      console.log('🛒 Shopping list generation result:', result);
      return result;
    }
    return null;
  });
  
  const { 
    addRecipeToMenu,
    removeRecipeFromMenu,
    clearMenu,
    addCustomIngredient,
    getMenuTotals
  } = useMenuOperations(weeklyMenu, recipes, updateMenu, resetShoppingList);

  // Enhanced function to trigger shopping list regeneration
  const enhancedUpdateMenu = async (updatedMenu: WeeklyMenu) => {
    console.log('🔄 Updating menu and regenerating shopping list...');
    await updateMenu(updatedMenu);
    
    // Force regenerate shopping list after menu update
    if (recipes && recipes.length > 0) {
      console.log('🛒 Force regenerating shopping list after menu update');
      setTimeout(async () => {
        const result = await generateShoppingList(updatedMenu, recipes);
        console.log('✅ Shopping list regenerated:', result);
      }, 500);
    }
  };

  // Enhanced function to add recipe and regenerate shopping list
  const enhancedAddRecipeToMenu = async (recipeId: string, day: string, mealType: string, portionSize: number) => {
    console.log('➕ Adding recipe to menu and regenerating shopping list...');
    await addRecipeToMenu(recipeId, day, mealType, portionSize);
    
    // Force regenerate shopping list after adding recipe
    if (weeklyMenu && recipes && recipes.length > 0) {
      console.log('🛒 Force regenerating shopping list after adding recipe');
      setTimeout(async () => {
        const result = await generateShoppingList(weeklyMenu, recipes);
        console.log('✅ Shopping list regenerated after recipe addition:', result);
      }, 500);
    }
  };

  // Enhanced function to remove recipe and regenerate shopping list
  const enhancedRemoveRecipeFromMenu = async (recipeId: string, day: string, mealType: string, index: number) => {
    console.log('➖ Removing recipe from menu and regenerating shopping list...');
    await removeRecipeFromMenu(recipeId, day, mealType, index);
    
    // Force regenerate shopping list after removing recipe
    if (weeklyMenu && recipes && recipes.length > 0) {
      console.log('🛒 Force regenerating shopping list after removing recipe');
      setTimeout(async () => {
        const result = await generateShoppingList(weeklyMenu, recipes);
        console.log('✅ Shopping list regenerated after recipe removal:', result);
      }, 500);
    }
  };
  
  // Load menu when user is available
  useEffect(() => {
    if (user?.id) {
      loadMenu();
    }
  }, [user?.id, loadMenu]);

  // Initialize meal types and nutrition goals when menu loads
  useEffect(() => {
    if (user?.id && !loading) {
      const profile = getSyncedProfile();
      if (profile) {
        // Set meal types based on profile
        const mealsPerDay = profile.meals_per_day || 3;
        const mealTypes = getMealTypes(mealsPerDay);
        setAvailableMealTypes(mealTypes);
        
        // Initialize nutrition goals if menu exists but doesn't have them
        if (weeklyMenu && !weeklyMenu.nutritionGoals?.calories) {
          const nutritionGoals = {
            calories: Number(profile.target_calories || profile.targetCalories) || 2000,
            protein: Number(profile.target_protein || profile.targetProtein) || calculateProteinFromWeight(Number(profile.weight) || 70),
            carbs: Number(profile.target_carbs || profile.targetCarbs) || 250,
            fat: Number(profile.target_fat || profile.targetFat) || 65,
            iron: Number(profile.target_iron || profile.targetIron) || 18,
            calcium: Number(profile.target_calcium || profile.targetCalcium) || 1000,
            zinc: Number(profile.target_zinc || profile.targetZinc) || 11,
            magnesium: Number(profile.target_magnesium || profile.targetMagnesium) || 400,
            omega3: Number(profile.target_omega3 || profile.targetOmega3) || 1.6
          };
          
          const updatedMenu = { 
            ...weeklyMenu,
            nutritionGoals: nutritionGoals
          };
          
          enhancedUpdateMenu(updatedMenu);
        }
      }
    }
  }, [weeklyMenu?.id, user?.id, loading, getSyncedProfile]);

  // Enhanced calorie suggestion that considers the total daily calories and number of meals
  const getCalorieSuggestion = (mealType: string): number => {
    const profile = getSyncedProfile();
    const totalCalories = weeklyMenu?.nutritionGoals?.calories || 
                         Number(profile?.target_calories || profile?.targetCalories) || 2000;
    const mealCount = availableMealTypes.length;
    
    console.log(`🎯 Calculating calories for ${mealType}: Total=${totalCalories}, Meals=${mealCount}`);
    
    if (mealCount <= 3) {
      // 3 meals or less: distribute evenly
      return Math.round(totalCalories / mealCount);
    } else if (mealCount === 4) {
      // 4 meals: breakfast 25%, lunch 35%, snack 15%, dinner 25%
      const distribution = {
        'breakfast': 0.25,
        'lunch': 0.35,
        'afternoon_snack': 0.15,
        'dinner': 0.25
      };
      return Math.round(totalCalories * (distribution[mealType] || 0.25));
    } else if (mealCount === 5) {
      // 5 meals: breakfast 20%, snack 10%, lunch 30%, snack 15%, dinner 25%
      const distribution = {
        'breakfast': 0.20,
        'morning_snack': 0.10,
        'lunch': 0.30,
        'afternoon_snack': 0.15,
        'dinner': 0.25
      };
      return Math.round(totalCalories * (distribution[mealType] || 0.20));
    } else {
      // 6+ meals: distribute more evenly with main meals getting slightly more
      const isMainMeal = ['breakfast', 'lunch', 'dinner'].includes(mealType);
      if (isMainMeal) {
        return Math.round(totalCalories * 0.25); // Main meals get 25%
      } else {
        const remainingCalories = totalCalories * 0.25; // 25% for all snacks
        const snackCount = mealCount - 3;
        return Math.round(remainingCalories / snackCount);
      }
    }
  };

  const value: MenuContextType = {
    weeklyMenu,
    loading,
    shoppingList,
    addRecipeToMenu: enhancedAddRecipeToMenu,
    removeRecipeFromMenu: enhancedRemoveRecipeFromMenu,
    clearMenu,
    getMenuTotals,
    availableMealTypes,
    getMealCalorieSuggestion: getCalorieSuggestion,
    addCustomIngredient,
    updateMenu: enhancedUpdateMenu,
    toggleShoppingItem,
    refreshMenu,
    forceUpdate
  };

  return (
    <MenuContext.Provider value={value}>
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
